import {
  clearReceiptHoldRemote,
  sendReceiptAccpayRemote,
  setReceiptHoldRemote,
  type RemoteAccpayPreview,
} from '../api/client.js';
import { putReceiptLocal, type LocalReceipt } from '../db/receiptsDb.js';
import { formatReceiptListAmount } from '../../../src/receipts/currency.js';
import {
  differingBillDatesLabel,
  formatReceiptDate,
  isoDateOnly,
} from '../../../src/receipts/receiptDate.js';
import { receiptPushDisplayState } from '../../../src/receipts/enqueueAccpay.js';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPence(p: number | null | undefined): string {
  if (p == null) return '—';
  return `£${(p / 100).toFixed(2)}`;
}

export function receiptBillReadbackHtml(item: LocalReceipt): string {
  const flags: string[] = [];
  if (item.xero_contact_map_mismatch) {
    flags.push(
      `Xero contact “${item.xero_contact_name ?? 'unknown'}” is not the mapped contact for ${item.supplier_name ?? 'this supplier'}. Not changed on the map.`,
    );
  }
  if (item.xero_total_mismatch) {
    flags.push(
      'Xero bill total no longer matches this receipt. Amounts were not changed.',
    );
  }
  if (item.xero_date_mismatch) {
    const pair = differingBillDatesLabel(
      item.receipt_date,
      item.xero_bill_date,
    );
    flags.push(
      pair
        ? `Xero bill date does not match this receipt date (${pair}). The date was not changed.`
        : 'Xero bill date does not match this receipt date. The date was not changed.',
    );
  }
  if (!item.xero_bill_id && item.xero_push_error) {
    flags.push(item.xero_push_error);
  }
  if (
    item.xero_has_attachments === false &&
    item.xero_attachment_status === 'outstanding'
  ) {
    flags.push('The receipt image is no longer on the Xero bill.');
  }
  const billDate = isoDateOnly(item.xero_bill_date);
  const hasBillFacts =
    item.xero_bill_status ||
    item.xero_invoice_number ||
    item.xero_contact_name ||
    item.xero_amount_due_pence != null ||
    billDate != null;
  if (!hasBillFacts && flags.length === 0) return '';
  const due =
    item.xero_bill_status === 'PAID'
      ? 'Settled'
      : `Due ${formatPence(item.xero_amount_due_pence)}`;
  return `<section class="receipt-xero-readback" data-xero-readback>
    <h2>On the Xero bill</h2>
    ${
      hasBillFacts
        ? `<dl class="receipt-xero-summary">
      ${item.xero_bill_status ? `<div><dt>Status</dt><dd>${escapeHtml(item.xero_bill_status)}</dd></div>` : ''}
      ${item.xero_invoice_number ? `<div><dt>Invoice no.</dt><dd>${escapeHtml(item.xero_invoice_number)}</dd></div>` : ''}
      ${item.xero_contact_name ? `<div><dt>Xero contact</dt><dd>${escapeHtml(item.xero_contact_name)}</dd></div>` : ''}
      ${billDate ? `<div><dt>Bill date</dt><dd>${escapeHtml(formatReceiptDate(billDate))}</dd></div>` : ''}
      ${item.xero_amount_due_pence != null || item.xero_bill_status === 'PAID' ? `<div><dt>Paid</dt><dd>${escapeHtml(due)}</dd></div>` : ''}
    </dl>`
        : ''
    }
    ${
      flags.length
        ? `<ul class="receipt-block-list">${flags
            .map((f) => `<li>${escapeHtml(f)}</li>`)
            .join('')}</ul>`
        : ''
    }
  </section>`;
}

function attachLabel(status: string | null | undefined): string {
  if (status === 'attached') return 'Image attached';
  if (status === 'outstanding') return 'Image not attached yet';
  if (status === 'skipped') return 'Image skipped (too large for Xero)';
  return 'Image not attached';
}

function summaryDl(preview: RemoteAccpayPreview): string {
  const account =
    preview.accountCode && preview.accountLabel
      ? `${preview.accountCode} ${preview.accountLabel}`
      : (preview.accountCode ?? preview.accountLabel ?? '—');
  return `<dl class="receipt-xero-summary">
    <div><dt>Supplier</dt><dd>${escapeHtml(preview.supplier)}</dd></div>
    <div><dt>Account</dt><dd>${escapeHtml(account)}</dd></div>
    <div><dt>Tax</dt><dd>${escapeHtml(preview.taxLabel ?? preview.taxType ?? '—')}</dd></div>
    <div><dt>Total</dt><dd>${escapeHtml(
      formatReceiptListAmount({
        currency: preview.currency,
        gross_pence: preview.grossPence,
        gbp_gross_pence: preview.gbpGrossPence,
      }),
    )}</dd></div>
    <div><dt>Contact</dt><dd>${escapeHtml(preview.contactLabel)}</dd></div>
  </dl>`;
}

export function receiptHoldSectionHtml(
  item: LocalReceipt,
  phase: 'idle' | 'holding' | 'releasing',
): string {
  if (item.held) {
    const reason = item.held_reason?.trim() || 'No reason stored';
    return `<section class="receipt-hold" data-receipt-hold>
      <div class="receipt-hold-banner" role="status">
        <p>Held from Xero. ${escapeHtml(reason)}</p>
      </div>
      <button type="button" class="btn-secondary btn-block" data-hold-release ${phase === 'releasing' ? 'disabled' : ''}>${phase === 'releasing' ? 'Releasing…' : 'Release hold'}</button>
    </section>`;
  }
  if (item.status !== 'confirmed' && item.status !== 'sent_to_accounts') {
    return '';
  }
  return `<section class="receipt-hold" data-receipt-hold>
    <label class="field">
      <span>Hold reason</span>
      <textarea data-hold-reason rows="2" maxlength="500" placeholder="Why this must not go to Xero"></textarea>
    </label>
    <button type="button" class="btn-secondary btn-block" data-hold-set ${phase === 'holding' ? 'disabled' : ''}>${phase === 'holding' ? 'Holding…' : 'Hold from Xero'}</button>
  </section>`;
}

export function bindReceiptHoldSection(
  root: HTMLElement,
  opts: {
    item: LocalReceipt;
    onChange: () => Promise<void>;
    onError: (text: string) => void;
  },
): void {
  const { item } = opts;
  root.querySelector('[data-hold-set]')?.addEventListener('click', () => {
    const raw =
      (
        root.querySelector('[data-hold-reason]') as HTMLTextAreaElement | null
      )?.value ?? '';
    const reason = raw.trim();
    if (!reason) {
      opts.onError('A hold needs a reason');
      return;
    }
    void (async () => {
      try {
        const remote = await setReceiptHoldRemote(item.id, reason);
        await putReceiptLocal({
          ...item,
          held: Boolean(remote.held),
          held_reason: remote.held_reason ?? reason,
          xero_push_outcome: remote.xero_push_outcome ?? item.xero_push_outcome,
          xero_push_error: remote.xero_push_error ?? item.xero_push_error,
          updatedAt: new Date().toISOString(),
        });
        await opts.onChange();
      } catch (err) {
        opts.onError(
          err instanceof Error ? err.message : 'Couldn’t hold this receipt',
        );
      }
    })();
  });

  root.querySelector('[data-hold-release]')?.addEventListener('click', () => {
    if (
      !window.confirm(
        'Release this hold? The receipt can then be sent to Xero.',
      )
    ) {
      return;
    }
    void (async () => {
      try {
        const remote = await clearReceiptHoldRemote(item.id);
        await putReceiptLocal({
          ...item,
          held: Boolean(remote.held),
          held_reason: remote.held_reason ?? null,
          xero_push_outcome: remote.xero_push_outcome ?? item.xero_push_outcome,
          xero_push_error: remote.xero_push_error ?? item.xero_push_error,
          updatedAt: new Date().toISOString(),
        });
        await opts.onChange();
      } catch (err) {
        opts.onError(
          err instanceof Error ? err.message : 'Couldn’t release this hold',
        );
      }
    })();
  });
}

export function xeroPushSectionHtml(
  item: LocalReceipt,
  preview: RemoteAccpayPreview | null,
  phase: 'idle' | 'confirming' | 'sending',
): string {
  if (item.held) {
    return '';
  }
  if (item.status !== 'confirmed' && item.status !== 'sent_to_accounts') {
    return '';
  }

  const billId = preview?.billId ?? item.xero_bill_id ?? null;
  const attach = preview?.attachmentStatus ?? item.xero_attachment_status ?? null;
  const xeroUrl = preview?.xeroUrl ?? null;
  const pushError = preview?.xeroPushError ?? item.xero_push_error ?? null;
  const pushOutcome = preview?.pushOutcome ?? item.xero_push_outcome ?? null;

  if (billId) {
    const retryAttach = attach === 'outstanding';
    return `<section class="receipt-xero-push" data-xero-push>
      <h2>Xero bill</h2>
      <p>Draft bill created. ${escapeHtml(attachLabel(attach))}.</p>
      ${
        pushError && attach !== 'attached'
          ? `<p class="hint">${escapeHtml(pushError)}</p>`
          : ''
      }
      ${
        xeroUrl
          ? `<p><a class="receipt-xero-link" href="${escapeHtml(xeroUrl)}" target="_blank" rel="noopener">View in Xero</a></p>`
          : `<p class="hint">Bill id ${escapeHtml(billId)}</p>`
      }
      ${
        retryAttach
          ? `<button type="button" class="submit" data-xero-retry ${phase === 'sending' ? 'disabled' : ''}>${phase === 'sending' ? 'Retrying…' : 'Retry attach'}</button>`
          : ''
      }
    </section>`;
  }

  const display = receiptPushDisplayState({
    status: item.status,
    xero_bill_id: billId,
    xero_push_outcome: pushOutcome,
    held: item.held,
    money_reconcile_overridden: item.money_reconcile_overridden,
  });

  if (display === 'queued' && phase === 'idle') {
    return `<section class="receipt-xero-push" data-xero-push>
      <h2>Queued for Xero</h2>
      <p>Will send on the next flush when you’re connected. Nothing else is waiting on you.</p>
      ${preview ? summaryDl(preview) : ''}
      <button type="button" class="submit" data-xero-send>Send now</button>
      <p class="hint">Retry if the queue is stuck. One tap here still confirms before posting.</p>
    </section>`;
  }

  if (pushOutcome === 'unknown') {
    const reason =
      pushError ??
      'Xero may already have this bill. Confirm before creating another.';
    if (phase === 'confirming') {
      return `<section class="receipt-xero-push" data-xero-push>
      <h2>Xero bill outcome unknown</h2>
      <p>${escapeHtml(reason)}</p>
      ${preview ? summaryDl(preview) : ''}
      <p>Create a new draft only if you have checked Xero and this bill is not there.</p>
      <button type="button" class="submit" data-xero-create-anyway>Confirm create anyway</button>
      <button type="button" class="btn-secondary btn-block" data-xero-cancel>Cancel</button>
    </section>`;
    }
    if (phase === 'sending') {
      return `<section class="receipt-xero-push" data-xero-push>
      <h2>Xero bill outcome unknown</h2>
      <p>Sending a new draft with a fresh key…</p>
      <button type="button" class="submit" disabled>Sending…</button>
    </section>`;
    }
    return `<section class="receipt-xero-push" data-xero-push>
      <h2>Xero bill outcome unknown</h2>
      <p>${escapeHtml(reason)}</p>
      ${preview ? summaryDl(preview) : ''}
      <button type="button" class="submit" data-xero-send>Create anyway…</button>
      <p class="hint">Will not reuse the expired key. Check Xero first.</p>
    </section>`;
  }

  if (!preview) {
    return `<section class="receipt-xero-push" data-xero-push>
      <h2>Send to Xero</h2>
      <p>Couldn’t load what would be created. Check the connection and retry.</p>
      <button type="button" class="submit" data-xero-send disabled>Send to Xero</button>
      <button type="button" class="btn-secondary btn-block" data-xero-reload>Retry</button>
    </section>`;
  }

  const ready = preview.liveReady && preview.contactAction !== 'blocked';
  const reason = preview.liveBlockReason ?? preview.contactLabel;

  if (phase === 'confirming' && ready) {
    return `<section class="receipt-xero-push" data-xero-push>
      <h2>Send to Xero</h2>
      <p>Create this draft bill in Xero?</p>
      ${summaryDl(preview)}
      <button type="button" class="submit" data-xero-confirm>Confirm send</button>
      <button type="button" class="btn-secondary btn-block" data-xero-cancel>Cancel</button>
    </section>`;
  }

  if (phase === 'sending') {
    return `<section class="receipt-xero-push" data-xero-push>
      <h2>Send to Xero</h2>
      <p>Sending draft bill…</p>
      ${summaryDl(preview)}
      <button type="button" class="submit" disabled>Sending…</button>
    </section>`;
  }

  return `<section class="receipt-xero-push" data-xero-push>
    <h2>Send to Xero</h2>
    ${summaryDl(preview)}
    <button type="button" class="submit" data-xero-send ${ready ? '' : 'disabled'}>Send to Xero</button>
    ${
      ready
        ? `<p class="hint">Creates a draft bill. Tap once more to confirm.</p>`
        : `<p class="hint">${escapeHtml(reason)}</p>`
    }
  </section>`;
}

export function bindXeroPushSection(
  root: HTMLElement,
  opts: {
    item: LocalReceipt;
    preview: RemoteAccpayPreview | null;
    phase: 'idle' | 'confirming' | 'sending';
    setPhase: (phase: 'idle' | 'confirming' | 'sending') => void;
    onChange: () => Promise<void>;
    onError: (text: string) => void;
  },
): void {
  const { item } = opts;
  if (item.held) return;
  if (item.status !== 'confirmed' && item.status !== 'sent_to_accounts') {
    return;
  }

  root.querySelector('[data-xero-reload]')?.addEventListener('click', () => {
    void opts.onChange();
  });

  root.querySelector('[data-xero-send]')?.addEventListener('click', () => {
    if ((root.querySelector('[data-xero-send]') as HTMLButtonElement)?.disabled) {
      return;
    }
    opts.setPhase('confirming');
  });

  root.querySelector('[data-xero-cancel]')?.addEventListener('click', () => {
    opts.setPhase('idle');
  });

  const post = async () => {
    opts.setPhase('sending');
    try {
      const createAnyway =
        (opts.preview?.pushOutcome ?? item.xero_push_outcome) === 'unknown';
      const result = await sendReceiptAccpayRemote(item.id, { createAnyway });
      const cur = item;
      await putReceiptLocal({
        ...cur,
        status: result.billId
          ? 'sent_to_accounts'
          : cur.status,
        xero_bill_id: result.billId,
        xero_attachment_status: result.attachmentStatus,
        xero_push_error: result.error,
        xero_push_outcome: result.confirmation.pushOutcome,
        updatedAt: new Date().toISOString(),
      });
      opts.setPhase('idle');
      if (result.error) {
        opts.onError(result.error);
      }
      await opts.onChange();
    } catch (err) {
      opts.onError(
        err instanceof Error ? err.message : 'Couldn’t send this receipt to Xero',
      );
      opts.setPhase('idle');
    }
  };

  root.querySelector('[data-xero-confirm]')?.addEventListener('click', () => {
    void post();
  });
  root.querySelector('[data-xero-create-anyway]')?.addEventListener('click', () => {
    void post();
  });
  root.querySelector('[data-xero-retry]')?.addEventListener('click', () => {
    void post();
  });
}
